import { type ReactElement } from 'react';
// Components
import { IndexChart } from '../index-chart/index-chart';
import { IndexSwitcher } from '../index-switcher/index-switcher';
import { LoginBlur } from '@utmb/design-system/login-blur/login-blur';
// Types
import { PiCategoryEnum } from '@utmb/types/enum/PiCategoryEnum';
import RaceYearResultForRunnerView from '@utmb/types/view/RaceYearResultForRunnerView';
// Styles
import styles from './index-statistics.module.scss';
import { Font } from '@utmb/design-system/text/font';
import { getColorStyle } from '@utmb/utils/colors';
import { ColorTypesEnum } from '@utmb/types/colors';
import { IndexKeyFigures } from '../index-key-figures.tsx/index-key-figures';
import { useTranslation } from 'next-i18next';
import { VALID_INDEX_THRESHOLD } from '@utmb/utils/utmb-index.constants';
import placeholderData from './placeholder-data.json';

const PLACEHOLDER_RESULTS = placeholderData as unknown as RaceYearResultForRunnerView[];

export interface IndexStatisticsProps {
    results: RaceYearResultForRunnerView[];
    selectedCategory: PiCategoryEnum;
    onCategoryChange: (category: PiCategoryEnum) => void;
    isAuthenticated: boolean;
}

export function IndexStatistics({ results, selectedCategory, onCategoryChange, isAuthenticated }: IndexStatisticsProps): ReactElement {
    const { t } = useTranslation();

    const displayedResults = isAuthenticated ? results : PLACEHOLDER_RESULTS;
    // Finisher-only results (index ≤ threshold) have no real score to plot.
    const chartResults = displayedResults.filter((r) => r.index == null || r.index > VALID_INDEX_THRESHOLD);

    return (
        <div className={styles.indexStatistics}>
            <div className={styles.header}>
                <Font.FuturaMedium as="h3" desktop="64" mobile="28" className={styles.title} color={getColorStyle(ColorTypesEnum.WHITE)}>
                    {t('singleRunner.statistics.global.evolution')}
                </Font.FuturaMedium>
                <IndexSwitcher value={selectedCategory} onChange={onCategoryChange} />
            </div>
            <LoginBlur isBlurred={!isAuthenticated}>
                <div className={styles.content}>
                    <div className={styles.wrapKeyFigures}>
                        <IndexKeyFigures results={displayedResults} category={selectedCategory} />
                    </div>
                    <div className={styles.wrapChart}>{chartResults.length > 0 ? <IndexChart results={chartResults} category={selectedCategory} /> : null}</div>
                </div>
            </LoginBlur>
        </div>
    );
}
